import { ACCOUNT_SERVICE_URL } from '@constants';
import useAuth from '@contexts/AuthProvider';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { FC, useLayoutEffect } from 'react';

import { CompaniesApi, Configuration, EmployeesApi, MeApi, PublicApi, RegistrationApi } from './generated';

const initialConfig = new Configuration({
  basePath: ACCOUNT_SERVICE_URL,
});

const apis = {
  companies: new CompaniesApi(initialConfig),
  employees: new EmployeesApi(initialConfig),
  registration: new RegistrationApi(initialConfig),
  me: new MeApi(initialConfig),
  public: new PublicApi(initialConfig),

  setAPIConfig: (config: Configuration) => {
    apis.companies = new CompaniesApi(config);
    apis.employees = new EmployeesApi(config);
    apis.registration = new RegistrationApi(config);
    apis.me = new MeApi(config);
    apis.public = new PublicApi(config);
  },
};

export default apis;

interface Props {
  children: React.ReactNode;
}

const queryCache = new QueryCache();
const mutationCache = new MutationCache();
const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

export const APILoader: FC<Props> = ({ children }) => {
  const { token } = useAuth();

  // useLayoutEffect so the auth header is applied before child query
  // effects fire. Effects run child-first, so a child's useQuery would otherwise fire
  // its request with a stale, header-less config; but React Query dispatches its fetch
  // in a passive effect, and all layout effects run before any passive effect.
  useLayoutEffect(() => {
    apis.setAPIConfig(
      new Configuration({
        basePath: ACCOUNT_SERVICE_URL,
        baseOptions: token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      }),
    );
  }, [token]);

  return (
    <QueryClientProvider client={queryClient}>
      {children} <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};
